import { ObjectId } from 'bson';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UserContext } from './App';
import { StateBoxProps } from './HSM';
import { ReadOnlyHSMView, ReadOnlyHSMViewProps } from './HSMRenderer.tsx';
import HSMRenderer, {
  HSMRendererProps,
  HSMView,
  HSMViewProps,
  ToolBar,
} from './HSMRenderer.tsx';
import { crumbsToUids, getStatesCurrentLayer } from './utils/stateUtils.tsx';

// traverses the json object, storing the uid as templateId and generating a new uid for each state
function templateActivityProps(parentProps: any) {
  if (!parentProps.children) {
    return parentProps;
  }

  const children = [...parentProps.children];

  // if(!children || children.length === 0) {
  //     parentProps.template = parentProps.uid;
  //     parentProps.uid = new ObjectId().toHexString();
  //     return parentProps;
  // }

  // for each child, do the update, then call recursively on its list of children
  for (let i = 0; i < children.length; i++) {
    children[i].template = children[i].uid;
    children[i].uid = new ObjectId().toHexString();
    children[i].children = templateActivityProps(children[i]);
  }

  return children;

  // return parentProps.children.map((v) => {
  //     return {...v, template: v.uid, uid: new ObjectId().toHexString(),
  //         children: templateActivityProps(parentProps.)};
  // })
}

function createHSMFromTemplate(
  activityProps: object,
  userContext: UserContext,
  viewMode: string,
  callBackFn: (value: Response) => void | PromiseLike<void>
) {
  // insert clone (without _id) into edit db
  let templatedProps = {
    ...activityProps,
    template: activityProps['_id'],
    author: userContext.id,
    viewMode: viewMode,
    children: templateActivityProps(activityProps),
  };

  fetch(`http://34.74.45.28:8080/hsm_edit/`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(templatedProps),
  }).then(callBackFn);
}

export type UseAsTemplateButtonProps = {
  activityProps: object;
  userContext: UserContext;
  isHumanMode: boolean;
  disabled: boolean;
};

export const UseAsTemplateButton = ({
  activityProps,
  userContext,
  isHumanMode,
  disabled,
}: UseAsTemplateButtonProps) => {
  function onCloneInserted(res: Response) {
    res.json().then((data) => {
      window.open('http://sparcs-box-342816.uk.r.appspot.com/edit/' + data._id);
    });
  }

  const PlusIcon = () => {
    return (
      <svg
        xmlns='http://www.w3.org/2000/svg'
        viewBox='-4.5 -4.5 24 24'
        width='24'
        fill='currentColor'
      >
        <path d='M8.9 6.9v-5a1 1 0 1 0-2 0v5h-5a1 1 0 1 0 0 2h5v5a1 1 0 1 0 2 0v-5h5a1 1 0 1 0 0-2h-5z'></path>
      </svg>
    );
  };

  const buttonClassName = 'btn btn-primary';
  const buttonText = 'Use as template';
  const viewingMode = isHumanMode ? 'human' : 'robot';

  return (
    <button
      disabled={disabled}
      onClick={() =>
        createHSMFromTemplate(
          JSON.parse(JSON.stringify(activityProps)),
          userContext,
          viewingMode,
          onCloneInserted
        )
      }
      className={buttonClassName}
    >
      <PlusIcon />
      {buttonText}
    </button>
  );
};

export type ViewModeProps = {
  isHumanMode: boolean;
  setIsHumanMode: React.Dispatch<React.SetStateAction<boolean>>;
  crumbIds: number[];
  setCrumbIds: React.Dispatch<React.SetStateAction<number[]>>;
};

export const ViewModeGroup = ({
  isHumanMode,
  setIsHumanMode,
  crumbIds,
  setCrumbIds,
}: ViewModeProps) => {
  function onHumanChange(e) {
    setIsHumanMode(true);

    if (crumbIds.length >= 3) {
      // if in human mode and on a comp skill or skill layer, return to top layer
      setCrumbIds([crumbIds[0]]);
    }
  }

  function onRobotChange(e) {
    setIsHumanMode(false);
  }

  return (
    <div className='viewmode'>
      <div className='btn-group'>
        <input
          type='radio'
          className='btn-check'
          name='options'
          id='radio1'
          autoComplete='off'
          checked={isHumanMode}
          onChange={onHumanChange}
        />
        <label className='btn btn-outline-primary' htmlFor='radio1'>
          Human caregiver
        </label>

        <input
          type='radio'
          className='btn-check'
          name='options'
          id='radio2'
          autoComplete='off'
          checked={!isHumanMode}
          onChange={onRobotChange}
        />
        <label className='btn btn-outline-primary' htmlFor='radio2'>
          Robot caregiver
        </label>
      </div>
    </div>
  );
};

export type ViewOnlyHSMRendererProps = {
  userContext: UserContext;
  nextUID: () => number;
  setStartingId: (id: number) => void;
  setCursor: React.Dispatch<React.SetStateAction<string>>;
};

export const ViewOnlyHSMRenderer = ({
  userContext,
  nextUID,
  setStartingId,
  setCursor,
}: ViewOnlyHSMRendererProps) => {
  const [showingCommentsFor, setShowingCommentsFor] = useState(-1);
  const [showingPapersFor, setShowingPapersFor] = useState(-1);

  const { id } = useParams();
  const [activityProps, setActivityProps] = useState({}); // for robot
  const [humanActivityProps, setHumanActivityProps] = useState({});
  const [templatedLayers, setTemplatedLayers] = useState([]);
  const [crumbIds, setCrumbIds] = useState([]);
  const [layer, setLayer] = useState(); // id of first state in layer
  const [hsmLoaded, setHsmLoaded] = useState(false);

  const [isHumanMode, setIsHumanMode] = useState(
    userContext.occupation !== 'Roboticist'
  );

  function setLayerWrapper(layer) {
    setTemplatedLayers([]);
    setLayer(layer);
  }

  // wrapper for hiding papers everytime comments are shown
  function showCommentsWrapper(id: number) {
    setShowingPapersFor(-1);

    if (id === showingCommentsFor) {
      // toggle comments off
      setShowingCommentsFor(-1);
    } else {
      setShowingCommentsFor(id);
    }
  }

  // wrapper for hiding comments everytime papers are shown
  function showPapersWrapper(id: number) {
    setShowingCommentsFor(-1);
    if (id === showingPapersFor) {
      // toggle comments off
      setShowingPapersFor(-1);
    } else {
      setShowingPapersFor(id);
    }
  }

  // This method fetches the hsms from the database.
  useEffect(() => {
    async function getHsm() {
      const response = await fetch(`http://34.74.45.28:8080/hsm_view/` + id);
      const responseHuman = await fetch(
        `http://34.74.45.28:8080/hsm_view_human/` + id
      );

      console.log(responseHuman);
      console.log('human ok:', responseHuman.ok);

      if (!response.ok) {
        const message = `An error occured: ${response.statusText}`;
        window.alert(message);
        return;
      }

      const hsmRes = await response.json();

      const hsmHumanRes = !responseHuman.ok
        ? hsmRes
        : await responseHuman.json();

      setHsmLoaded(true);
      setActivityProps(hsmRes);
      setHumanActivityProps(hsmHumanRes);
      setCrumbIds([hsmRes.id]);

      if (hsmRes.children) {
        setLayer(hsmRes.children[0].uid);
      }
      setStartingId(hsmRes.id);
    }

    getHsm();

    return;
  }, [hsmLoaded]);

  useEffect(() => {
    // TODO maybe only get templates of the same viewing mode?
    if (crumbIds.length > 1) {
      const getTemplatesParams = {
        _id: activityProps['_id'],
        crumbs: crumbsToUids(activityProps, crumbIds, []),
        layer: getStatesCurrentLayer(activityProps, crumbIds),
      };

      fetch(`http://34.74.45.28:8080/getTemplates`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(getTemplatesParams),
      }).then((res) => {
        if (res.ok) {
          res.json().then((v) => {
            setTemplatedLayers(v);
          });
        }
      });
    }
  }, [layer]);

  if (!hsmLoaded) {
    return null;
  }

  // if in robot mode, we use the robot workflow, otherwise, we use the human workflow
  let rendererActivityProps = isHumanMode ? humanActivityProps : activityProps;
  let rendererSetActivityProps = isHumanMode
    ? setHumanActivityProps
    : setActivityProps;

  const ViewOnlyHSMRendererToolbar = () => {
    const templateDisabled =
      !isHumanMode && userContext.occupation !== 'Roboticist';
    return (
      <ToolBar>
        <UseAsTemplateButton
          activityProps={rendererActivityProps}
          userContext={userContext}
          isHumanMode={isHumanMode}
          disabled={templateDisabled}
        />
      </ToolBar>
    );
  };

  const hsmViewProps: HSMViewProps = {
    activityProps: rendererActivityProps,
    setActivityProps: rendererSetActivityProps,
    crumbIds: crumbIds,
    setCrumbIds: setCrumbIds,
    setLayer: setLayerWrapper,
    discussionButtons: true,
    viewOnlyMode: false,
    editing: false,
    setCursor: setCursor,
    nextUID: nextUID,
    setShowingCommentsFor: showCommentsWrapper,
    setShowingPapersFor: showPapersWrapper,
    setIsUnsavedChanges: () => {},
    isHumanMode: isHumanMode,
  };

  let readOnlyHsmViews = templatedLayers.map((v) => {
    return (
      <ReadOnlyHSMView
        info={v[0]}
        statesProps={v.slice(1)}
        humanMode={isHumanMode}
      />
    );
  });

  const hsmRendererProps: HSMRendererProps = {
    userContext: userContext,
    activityId: id,
    activityProps: rendererActivityProps,
    setActivityProps: rendererSetActivityProps,
    showingCommentsFor: showingCommentsFor,
    setShowingCommentsFor: setShowingCommentsFor,
    showingPapersFor: showingPapersFor,
    setShowingPapersFor: setShowingPapersFor,
    crumbIds: crumbIds,
    setCrumbIds: setCrumbIds,
    setLayer: setLayerWrapper,
    setCursor: setCursor,
    toolbar: <ViewOnlyHSMRendererToolbar />,
    children: [<HSMView {...hsmViewProps} />, ...readOnlyHsmViews],
    editing: false,
    isHumanMode: isHumanMode,
    center: () => (
      <ViewModeGroup
        isHumanMode={isHumanMode}
        setIsHumanMode={setIsHumanMode}
        crumbIds={crumbIds}
        setCrumbIds={setCrumbIds}
      />
    ),
  };

  return <HSMRenderer {...hsmRendererProps} />;
};

export default ViewOnlyHSMRenderer;
